/**
 * VIEW BASE — Shared DOM utilities.
 * addEventListener calls for all static HTML elements live here.
 * Presenter injects callbacks — view.base has zero business logic.
 */

import { escapeHtml } from '../utils.js';

// ─── Toast ────────────────────────────────────────────────────────────────────

export function showToast(message, type = 'info') {
  const container = document.getElementById('toast-container');
  if (!container) return;
  const icons = { success: 'fa-circle-check', error: 'fa-circle-exclamation', info: 'fa-circle-info' };
  const toast = document.createElement('div');
  toast.className = `toast-msg ${type}`;
  toast.innerHTML = `<i class="fa-solid ${icons[type] || icons.info}"></i> <span>${escapeHtml(message)}</span>`;
  container.appendChild(toast);
  setTimeout(() => { toast.style.opacity = '0'; setTimeout(() => toast.remove(), 300); }, 3500);
}

// ─── Page switching ───────────────────────────────────────────────────────────

export function showPage(pageId) {
  document.querySelectorAll('.page-view').forEach(p => p.classList.add('hidden'));
  document.getElementById(`page-${pageId}`)?.classList.remove('hidden');
}

// ─── Button loading state ─────────────────────────────────────────────────────

export function setButtonLoading(btnId, loading, defaultHtml) {
  const btn = document.getElementById(btnId);
  if (!btn) return;
  btn.disabled  = loading;
  btn.innerHTML = loading
    ? `<i class="fa-solid fa-spinner fa-spin"></i> لطفا صبر کنید...`
    : defaultHtml;
}

// ─── Map selected address display ─────────────────────────────────────────────

export function updateMapAddress(address) {
  const el = document.getElementById('map-selected-address');
  if (el) el.textContent = address || '';
}

// ─── Static event bindings ────────────────────────────────────────────────────
// All elements with a fixed id in HTML are bound here once on init.
// Callbacks are injected by Presenter — no logic lives in this file.

export function bindStaticEvents(callbacks) {

  // ── data-navigate delegation ─────────────────────────────────────────────
  // Single delegated listener handles ALL back buttons and "return to menu"
  // buttons via data-navigate="pageName" attribute — no onclick="" needed.
  document.addEventListener('click', e => {
    const target = e.target.closest('[data-navigate]');
    if (!target) return;
    const page = target.getAttribute('data-navigate');
    if (page) callbacks.onNavigate(page);
  });

  // ── Addon sheet ──────────────────────────────────────────────────────────
  document.getElementById('btn-submit-addons')?.addEventListener('click',     callbacks.onAddonSubmit);
  document.getElementById('btn-close-sheet')?.addEventListener('click',       callbacks.onAddonClose);
  document.getElementById('bottom-sheet-backdrop')?.addEventListener('click', callbacks.onAddonClose);

  // ── Invoice ──────────────────────────────────────────────────────────────
  document.getElementById('btn-clear-cart')?.addEventListener('click',     callbacks.onClearCart);
  document.getElementById('btn-apply-coupon')?.addEventListener('click',   callbacks.onApplyCoupon);
  document.getElementById('btn-invoice-submit')?.addEventListener('click', callbacks.onInvoiceSubmit);
  document.getElementById('order-notes')?.addEventListener('input', e => callbacks.onNotesChange(e.target.value));

  // ── Floating cart ────────────────────────────────────────────────────────
  document.getElementById('floating-cart')?.addEventListener('click', callbacks.onFloatingCartClick);

  // ── OTP ──────────────────────────────────────────────────────────────────
  document.getElementById('btn-send-otp')?.addEventListener('click',   callbacks.onSendOtp);
  document.getElementById('btn-verify-otp')?.addEventListener('click', callbacks.onVerifyOtp);
  document.getElementById('btn-resend-otp')?.addEventListener('click', callbacks.onResendOtp);
  // btn-change-phone uses data-navigate="otp" — handled by delegation above
  // but also needs special logic (clear OTP state), so we add it here too
  document.getElementById('btn-change-phone')?.addEventListener('click', callbacks.onChangePhone);

  // OTP digit auto-focus chain
  document.querySelectorAll('.otp-digit-box').forEach((box, idx, boxes) => {
    box.addEventListener('input', e => {
      e.target.value = e.target.value.replace(/[^0-9]/g, '');
      if (e.target.value.length === 1 && idx < boxes.length - 1) boxes[idx + 1].focus();
    });
    box.addEventListener('keydown', e => {
      if (e.key === 'Backspace' && !box.value && idx > 0) boxes[idx - 1].focus();
    });
  });

  // ── Checkout ─────────────────────────────────────────────────────────────
  document.getElementById('btn-checkout-submit')?.addEventListener('click', callbacks.onCheckoutSubmit);

  // ── Payment failed ───────────────────────────────────────────────────────
  document.getElementById('btn-retry-payment')?.addEventListener('click',    callbacks.onRetryPayment);
  document.getElementById('btn-failed-back-menu')?.addEventListener('click', callbacks.onBackToMenu);

  // ── Logout (optional — add id="btn-logout" anywhere in HTML) ─────────────
  document.getElementById('btn-logout')?.addEventListener('click', callbacks.onLogout);
}
